import React, { useRef, useEffect } from 'react';
import { Plus, Copy, Layout } from 'lucide-react';
import { CreateTabOptions, TabTemplate, TAB_TEMPLATES } from '../../types/tabs.types';

interface CreateTabMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (options: CreateTabOptions) => void;
  position: { x: number; y: number };
}

const CreateTabMenu: React.FC<CreateTabMenuProps> = ({ isOpen, onClose, onCreate, position }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleTemplateCreate = (template: TabTemplate) => {
    onCreate({ templateType: template });
    onClose();
  };

  const handleCopyCreate = () => {
    onCreate({ copyFromActiveTab: true });
    onClose();
  };

  const handleBlankCreate = () => {
    onCreate({});
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 w-64"
      style={{
        right: window.innerWidth - position.x,
        top: position.y,
      }}
    >
      <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        Templates
      </div>
      {Object.entries(TAB_TEMPLATES).map(([key, template]) => (
        <button
          key={key}
          onClick={() => handleTemplateCreate(key as TabTemplate)}
          className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <div className="flex items-start gap-2">
            <Layout className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">{template.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 break-words">
                {template.description}
              </div>
            </div>
          </div>
        </button>
      ))}

      <hr className="border-gray-200 dark:border-gray-600 my-1" />

      <button
        onClick={handleCopyCreate}
        className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        <Copy className="h-4 w-4" />
        Copy Current Tab
      </button>

      <button
        onClick={handleBlankCreate}
        className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        Blank Dashboard
      </button>
    </div>
  );
};

export default CreateTabMenu;
